// src/store/authStore.ts
import { create } from "zustand";
import { db } from "@/lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import type { AppUser } from "@/types/user";
import type { Organization } from "@/types/organization";

interface AuthState {
  user: AppUser | null;
  organization: Organization | null;
  structure: any[];           // OrgNode[]
  isAuthenticated: boolean;
  loading: boolean;

  init: () => Promise<void>;
  login: (login: string, password: string) => Promise<boolean>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  organization: null,
  structure: [],
  isAuthenticated: false,
  loading: false,

  init: async () => {
    console.log("🔄 authStore: init выполнен");
  },

  login: async (loginInput: string, password: string): Promise<boolean> => {
    const cleanLogin = loginInput.trim();
    console.log(`🔑 Попытка входа: "${cleanLogin}"`);

    try {
      const q = query(collection(db, "users"), where("login", "==", cleanLogin));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log("❌ Пользователь не найден");
        return false;
      }

      const userDoc = snapshot.docs[0];
      const userData = userDoc.data();

      if (userData.password !== password) {
        console.log("❌ Неверный пароль");
        return false;
      }

      const user: AppUser = {
        id: userDoc.id,
        organizationId: userData.organizationId || "orv-vsks",
        numericId: Number(userData.numericId || userDoc.id),
        login: userData.login,
        password: userData.password,
        name: userData.name,
        role: userData.role || "Сотрудник",
        canCreateTask: userData.canCreateTask ?? false,
        isAdmin: userData.isAdmin ?? false,
        active: userData.active ?? true,
        createdAt: userData.createdAt,
        lastLogin: new Date().toISOString(),
        avatarUrl: userData.avatarUrl,
      };

      // Обновляем lastLogin
      await updateDoc(doc(db, "users", userDoc.id), {
        lastLogin: new Date().toISOString(),
      });

      // Загружаем организацию
      let organization: Organization | null = null;
      let structure: any[] = [];

      if (user.organizationId) {
        const orgDocRef = doc(db, "organizations", user.organizationId);
        const orgSnap = await getDoc(orgDocRef);

        if (orgSnap.exists()) {
          organization = { id: orgSnap.id, ...orgSnap.data() } as Organization;
          structure = organization.structure || [];
        } else {
          console.warn(`⚠️ Организация ${user.organizationId} не найдена. Используем fallback.`);
        }
      }

      // Если структура пока пустая — используем legacy заглушку (чтобы не сломать сайдбар)
      if (structure.length === 0) {
        structure = [
          { id: "legacy-1", name: "Заместитель руководителя", personName: "Андреев Алексей Евгеньевич", icon: "Crown", order: 1 },
          { id: "legacy-2", name: "Начальник отдела", personName: "Багаутдинов Ахмет Айратович", icon: "Bomb", order: 2 },
        ];
      }

      set({
        user,
        organization,
        structure,
        isAuthenticated: true,
      });

      console.log(`🎉 Вход выполнен → ${user.name} | Организация: ${organization?.shortName || "orv-vsks (fallback)"}`);
      return true;

    } catch (err: any) {
      console.error("🚨 Ошибка входа:", err);
      return false;
    }
  },

  logout: () => {
    set({
      user: null,
      organization: null,
      structure: [],
      isAuthenticated: false,
    });
  },
}));